/**
 * The toolbar under the menu bar, like Unreal Engine's main toolbar: the buttons the services
 * contribute (`toolbar` in `app/subsystems.ts`, gathered by `state/shell.ts`), in groups
 * separated by a line. Each button shows a picture and a word; its tooltip names its shortcut.
 * A button is greyed out or pressed as its service says, at every moment; one opening a window
 * the environment worked in leaves out (Analyze in Writing) is not shown there.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "./context";
import { Icon } from "../shared/icons";
import type { ToolbarButton } from "../shared/menus";
import { windowOffered } from "../state/environments";

/** The toolbar. */
export function Toolbar(): JSX.Element {
  const app = useApp();
  const { shell, commands, environments, wm } = app;
  /** Whether a button belongs in the environment worked in (a button opening a window it leaves out does not). */
  const offered = (b: ToolbarButton) => b.command !== "window.open" || !b.argument || windowOffered(environments.current(), b.argument, wm.windows().find((w) => w.id === b.argument)?.group);
  /** The groups of buttons shown, empty ones left out. */
  const groups = () => shell.toolbar().map((g) => g.filter(offered)).filter((g) => g.length > 0);
  /** A button's tooltip: its title (or label) and its shortcut. */
  const tooltip = (b: ToolbarButton) => {
    const keys = app.keys.shortcut(b.command, b.argument ?? null);
    const text = b.title ?? b.label;
    return keys ? `${text} (${keys})` : text;
  };
  /** Runs a button. */
  const press = (b: ToolbarButton) => {
    if (b.action) b.action();
    else if (b.command) commands.run(b.command, b.argument ?? null);
  };
  return (
    <div class="toolbar" id="toolbar" role="toolbar" aria-label="Tools">
      <For each={groups()}>
        {(group, i) => (
          <>
            <Show when={i() > 0}>
              <span class="tb-sep" aria-hidden="true" />
            </Show>
            <For each={group}>
              {(b) => (
                <button
                  type="button"
                  class="tb-button"
                  id={b.id}
                  data-tool={b.id}
                  title={tooltip(b)}
                  disabled={b.disabled?.() ?? false}
                  aria-pressed={b.pressed ? b.pressed() : undefined}
                  onClick={() => press(b)}
                >
                  <Icon name={b.icon} />
                  <span>{b.label}</span>
                </button>
              )}
            </For>
          </>
        )}
      </For>
    </div>
  );
}
